import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import { autoinject, bind, inject, TasksStore, toArray } from 'Mri.Web.Common';
import * as React from 'react';
import Select from 'react-select';
import { ValueType } from 'react-select/src/types';
import { AccountStore } from '../Account/AccountStore';
import { AccountsStore } from '../Accounts/AccountsStore';
import { AppStore } from '../Core/AppStore';
import { UsersStore } from './UsersStore';

interface IAccountOption {
    value: string;
    label: string;
}

export interface IUserAccountsCardProps {
    readonly user: Mri.Web.Portal.Controllers.Account.UserModel;
}

@autoinject
@observer
export class UserAccountsCard extends React.Component<IUserAccountsCardProps> {
    public render() {
        return (
            <div className="card h-100">
                <div className="card-header bg-btn-primary">Accounts</div>
                <div className="scroll-container minh-200">
                    <div className="scroll-card p-0">
                        <table className="table-striped w-100">
                            <tbody>
                                {this.props.user.accounts.length ? (
                                    this.props.user.accounts.map(a => (
                                        <AccountRow key={a} accountNumber={a} user={this.props.user} />
                                    ))
                                ) : (
                                    <tr className="w-100 text-center">
                                        <td colSpan={2}>No Current Accounts</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
                <div className="mt-auto p-2 bd-highlight">
                    <div className="d-flex">
                        <Select
                            className="react-select-container d-inline-block flex-grow-1"
                            classNamePrefix="react-select"
                            options={this.accountOptions}
                            onChange={this.onSelectAccount}
                        />

                        <button
                            className="btn btn-primary d-inline-block ms-2"
                            onClick={this.addAccount}
                            disabled={
                                this.selectedAccount
                                    ? this.props.user.accounts.includes(this.selectedAccount.value)
                                    : true
                            }
                        >
                            {this.loading ? 'Adding...' : 'Add'}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    @bind
    @action
    private async addAccount(event: React.FormEvent<HTMLButtonElement>) {
        this.loading = true;
        const task = this.tasksStore.startSessionTask(`Add Account`);

        if (!this.selectedAccount) throw new Error('An account must be selected');

        const account: Mri.Web.Portal.Controllers.Users.AddAccountModel = {
            account: this.selectedAccount.value,
        };

        if (this.props.user) {
            this.loading = true;
            try {
                await this.usersStore.addAccountAsync(this.props.user, account);
                task.complete();
            } catch (e: any) {
                task.error(e);
            }
        }
        this.loading = false;
    }

    @bind
    @action
    private onSelectAccount(newValue: ValueType<IAccountOption, false>) {
        if (!Array.isArray(newValue)) {
            const value = newValue as IAccountOption | null | undefined;
            // TODO bug in typescript where Array.isArray works in discriminating the union type
            // however !Array.isArray fails to discriminate... Weird...
            this.selectedAccount = value ?? undefined;
        }
    }

    @computed
    private get accountOptions(): IAccountOption[] {
        const accounts = toArray(this.accountsStore.accounts)
            .filter(a => this.props.user?.accounts.indexOf(a.accountNumber) == -1)
            .map(a => {
                return {
                    value: a.accountNumber,
                    label: a.accountName,
                };
            });
        return accounts;
    }

    @observable
    private loading: boolean = false;
    @observable
    private selectedAccount?: IAccountOption;
    @inject
    private accountsStore!: AccountsStore;
    @inject
    private usersStore!: UsersStore;
    @inject
    private tasksStore!: TasksStore;
}

export interface IAccountRowProps {
    readonly accountNumber: string;
    readonly user: Mri.Web.Portal.Controllers.Account.UserModel;
}

@autoinject
@observer
export class AccountRow extends React.Component<IAccountRowProps> {
    public render() {
        return (
            <tr>
                <td className="ps-2">{this.accountTitle}</td>

                <td>
                    {this.canRemove && (
                        <a className="btn text-danger fw-bold float-end" onClick={this.removeAccount}>
                            {this.loading ? 'Removing...' : 'Remove'}
                        </a>
                    )}
                </td>
            </tr>
        );
    }

    @bind
    @action
    private async removeAccount() {
        this.loading = true;
        const task = this.tasksStore.startSessionTask(`Remove Account ${this.accountTitle}`);

        try {
            await this.usersStore.removeAccountAsync(this.props.user, this.props.accountNumber);
            task.complete();
        } catch (e: any) {
            task.error(e);
        }

        this.loading = false;
    }

    @computed
    private get canRemove() {
        if (this.accountStore.isInRole('MriAdmin')) return true;

        if (this.props.user.accounts.length <= 1) return false;

        if (!this.accountStore.user) return false;

        return this.accountStore.user!.accounts.includes(this.props.accountNumber);
    }

    @computed
    private get account() {
        return this.accountsStore.accounts.get(this.props.accountNumber);
    }

    @computed
    private get accountTitle() {
        return this.account?.accountName ?? this.props.accountNumber;
    }

    @observable
    private loading: boolean = false;
    @inject
    private usersStore!: UsersStore;
    @inject
    private tasksStore!: TasksStore;
    @inject
    private accountStore!: AccountStore;
    @inject
    private accountsStore!: AccountsStore;
}
